import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatValue } from "./Utils";
import _ from "underscore";
import moment from "moment";
import { fillColors } from "../constants.js";



//return the groupings in the order of the measure, including "Other" if the number of groupings is more than the max groupings when specified
function getGroupings(data, maxGroupings, includeOthers = false) {
	var groupings = [];
	var groupedData = _.groupBy(data, "grouping");
	for (const group in groupedData) {
		let groupSum = groupedData[group].reduce((acc, curr) => acc + curr.measure, 0);

		groupings.push({
			grouping: group,
			measure: groupSum,
		});
	}

	groupings.sort((a, b) => b.measure - a.measure);

	let returnValue = groupings.map(item => item.grouping).slice(0, maxGroupings);

	// if the number of groupings is more than the max groupings and other should be included, add "Other" value
	if (includeOthers && returnValue.length >= maxGroupings) {
		returnValue.push("Other");
	}

	return returnValue;
}

function keepOnlyTopGroupings(data, groupings) {
	var result = [];

	if (data && data.length > 0) {
		data.forEach(item => {
			if (groupings.find(group => group === item.grouping)) {
				result.push(item);
			} else {
				result.push({
					...item,
					grouping: "Other"
				})
			}
		});
	}

	return result;
}

function prepareData(data, maxGroupings, dateFormat) {

	// check whether the grouping has more than 10 values and order the grouping accordingly
	const groupings = getGroupings(data, maxGroupings);	
	const preparedData = keepOnlyTopGroupings(data, groupings);

	var d = _.groupBy(preparedData, "time");

	var result = [];

	for (var key in d) {
		var o = {};
		var total = 0;

		for (var i = 0; i < d[key].length; i++) {
			// if the grouping is not in the object, add it with a value of 0
			if (o[d[key][i].grouping] === undefined) {
				o[d[key][i].grouping] = 0;
			}

			// add the measure to the grouping
			o[d[key][i].grouping] += d[key][i].measure;
			total += d[key][i].measure;
		}


		var formattedDate = moment(key).format(dateFormat);

		result.push({
			_name: formattedDate,
			_original: key,
			_total: total,
			...o,
		});
	}

	// console.log(result);

	return result;
}

export const BarChartComponent = (props) => {
	const { data, title,  dateFormat = "MMM D", legendPosition = "bottom" } = props;


	const maxGroupings = 10;
	const pdata = prepareData(data, maxGroupings, dateFormat);
	const sortedHeaders = getGroupings(data, maxGroupings, true);	
	
	const verticalAlign = legendPosition === "right" ? "top" : "bottom";
    const align = legendPosition === "right" ? "right" : "center";
    const layout = legendPosition === "right" ? "vertical" : "horizontal";

	const bars = sortedHeaders.map((header, idx) => (
		<Bar
			barSize={30}
			type="monotone"
			key={header}
			dataKey={header}
			fill={fillColors[idx]}
			stackId="a"
			radius={[8, 8, 5, 5]}
		/>
	));
	


	
	return (
		<div className="bg-card rounded-card border border-card-bd my-2 mr-4 p-4 mt-8">
			<div className="text-text text-lg font-semibold mt-1 cursor-default" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
				{title}
			</div>
			{pdata && pdata.length > 0 ? (
				<ResponsiveContainer width="100%" height={340}>
					<BarChart
						data={pdata}
						margin={{
							top: 30,
							left: 0,
							bottom: 20,
							right: 40,
						}}
					>
						{/* <CartesianGrid strokeDasharray="3 3" /> */}
						<XAxis dataKey="_name" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" />
						<YAxis tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={formatYAxis} />
						<Tooltip
							content={(props) => renderTooltip({...props, dateFormat})}
							cursor={{ fill: "hsl(var(--chart-grid))" }}
							// cursor={{fill: 'transparent'}}
						/>

						{bars}

						<Legend
							verticalAlign={verticalAlign}
							align={align}
							layout={layout}
							// height={20}
							iconType="circle"
							formatter={renderLegend}
							wrapperStyle={{ paddingLeft: 10 }}
						/>
					</BarChart>
				</ResponsiveContainer>
			) : (
				<div className="h-[340px] flex items-center justify-center text-text-2">
					No data available
				</div>
			)}
		</div>
	);
};

function formatYAxis(value) {
	return formatValue(value, "currency", false);
}

const renderLegend = (value, entry) => {
	return <span className="text-text-2 text-xs">{value}</span>;
};

const renderTooltip = ({ active, payload, dateFormat }) => {
	const items = [];

	if (active && payload && payload.length) {
		for (var i = 0; i < payload.length; i++) {
			items.push(
				<div className="value flex flex-row" key={i}>
					<svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" className="">
						<rect x="0" y="8" width="6" height="6" fill={payload[i].fill} />
					</svg>

					<span className="flex-1 mr-4">{`${payload[i].name}`}</span>
					<span className="text-right">{`${formatValue(payload[i].value, "currency", false )}`}</span>
				</div>
			);
		}

		return (
			<div className="custom-tooltip bg-surface rounded-card border border-card-bd shadow-float p-1 minw-64 ml-8 py-4 px-6">
				<div className="flex flex-row mb-2">
					<p className="flex-1 label font-semibold mr-4">
						{`${moment(payload[0].payload._original).format(dateFormat)}`}
					</p>
					<p className="text-right label font-semibold">
						{`${formatValue(payload[0].payload._total, "currency", false)}`}
					</p>
				</div>
				{items}
			</div>
		);
	}

	return null;
};
